import React, { createRef, Fragment, FunctionComponent, useEffect, useState } from "react";
import styles from "./VideoViewer.module.scss";
import VideoViewerProps from "./VideoViewerProps.interface";

import combineClasses from "helpers/combine-classes";
import setFileOptions from "app/media/helpers/setFileOptions";
import IconViewer from "components/IconViewer/IconViewer";
import setImageOptions from "app/media/helpers/setImageOptions";
import { VideoLoadingState } from "./video-loading.enum";

const VideoViewer: FunctionComponent<VideoViewerProps> = (props): JSX.Element => {
    const videoUrl = setFileOptions(props.src);
    const posterUrl = props.poster ? setImageOptions(props.poster, 1280, "jpg", undefined, undefined, undefined) : undefined;
    const video = createRef<HTMLVideoElement>();

    const isAutoplay = props.autoplay ? props.autoplay : false;
    const isLoop = props.loop ? props.loop : false;
    const showControls = props.controls ? props.controls : false;

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const isPlayingClass = isPlaying === true ? styles.is_playing : undefined;

    const aspectRatioStyle = (props.aspectRatioX && props.aspectRatioY) ? { aspectRatio: `${props.aspectRatioX}/${props.aspectRatioY}` } : undefined;

    const [videoLoadingState, setVideoLoadingState] = useState<VideoLoadingState>(VideoLoadingState.LOADING);
    const onLoadedClass = videoLoadingState === VideoLoadingState.LOADED ? props.loadedClassName : undefined;

    useEffect(() => {
        setTimeout(() => {
            setVideoLoadingState(VideoLoadingState.LOADED);
        }, 250);
    }, []);

    const toggleVideoHandler = () => {
        const videoCurrent = video.current;
        setIsPlaying(!isPlaying);

        //Plays or pauses the current video
        { videoCurrent && !isPlaying ? videoCurrent?.play() : videoCurrent?.pause(); }
    };

    const onLoadStartHandler = (event: any) => {
        if (event && event.target)
            event.target.volume = isAutoplay ? 0 : 1;
    };

    useEffect(() => {
        if (!video.current) {
            return;
        }

        if (isMuted) {
            video.current.defaultMuted = true;
            video.current.muted = true;
        }
        else {
            video.current.defaultMuted = false;
            video.current.muted = false;
            video.current.volume = 1;
        }

        // video.current.srcObject = props.src;
    }, [isMuted]);

    const onMutedButtonClicked = (value: boolean) => {
        setIsMuted(value);

        if (video.current) {
            video.current.currentTime = 0;
        }
    };

    const onFullscreenButtonClicked = () => {
        if (video.current) {
            video.current.currentTime = 0;

            if ((video.current as any).requestFullScreen) {
                (video.current as any).requestFullScreen();
            }
            else if ((video.current as any).webkitRequestFullScreen) {
                (video.current as any).webkitRequestFullScreen();
            }
            else if ((video.current as any).mozRequestFullScreen) {
                (video.current as any).mozRequestFullScreen();
            }
            else if ((video.current as any).msRequestFullscreen) {
                (video.current as any).msRequestFullscreen();
            }
            else if ((video.current as any).webkitEnterFullscreen) {
                (video.current as any).webkitEnterFullscreen();
            }
        }
    };

    return (
        <Fragment>
            <div className={combineClasses(styles.wrapper, onLoadedClass, props.className)} style={aspectRatioStyle}>
                <div data-name="inner" className={styles.inner}>
                    <div className={styles.toolbar}>
                        {/* Icon: Sound */}
                        {props.soundIcon ? (
                            <button className={styles.sound_button} onClick={() => onMutedButtonClicked(!isMuted)}>
                                {isMuted ? (
                                    <IconViewer name="sound-off" aspectRatioX={96.071248} aspectRatioY={92.941124} />
                                ) : (
                                    <IconViewer name="sound-on" aspectRatioX={91.789998} aspectRatioY={92.941124} />
                                )}
                            </button>
                        ) : null}

                        {/* Icon: Fullscreen */}
                        {props.fullscreenIcon ? (
                            <button className={styles.fullscreen_button} onClick={onFullscreenButtonClicked}>
                                <IconViewer name="fullscreen" />
                            </button>
                        ) : null}
                    </div>

                    {props.src ? (
                        <video data-name="video" className={styles.video} poster={posterUrl} controls={(isAutoplay && showControls) || (!isAutoplay && isPlaying)} autoPlay={isAutoplay} loop={isLoop} playsInline={isAutoplay} muted={isMuted} ref={video} onLoadStart={onLoadStartHandler}>
                            <source src={videoUrl} type="video/mp4" />
                            {props.alt}
                        </video>
                    ) : null}

                    {!isAutoplay ? (
                        <div className={combineClasses(styles.button_wrapper, isPlayingClass)} onClick={() => toggleVideoHandler()}>
                            <button type="button" className={styles.button}>
                                {!isPlaying ? (
                                    <IconViewer name="play" />
                                ) : (
                                    <IconViewer name="pause" />
                                )}
                            </button>
                        </div>
                    ) : null}

                    {typeof (props.aspectRatioX) !== "undefined" && typeof (props.aspectRatioY) !== "undefined" ? (
                        <svg viewBox={`0 0 ${props.aspectRatioX} ${props.aspectRatioY}`} version="1.1" className={styles.ratio} preserveAspectRatio="xMidYMid" xmlSpace="preserve"></svg>
                    ) : null}
                </div>
            </div>
        </Fragment>
    );
};

export default VideoViewer;